import React from 'react';
import Select, { StylesConfig, SingleValue, MultiValue } from 'react-select';

export interface Option {
  value: string | number;
  label: string;
  isDisabled?: boolean;
}

interface Select2Props {
  options: Option[];
  value?: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  isMulti?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  className?: string;
  menuPlacement?: 'auto' | 'bottom' | 'top';
}

const Select2: React.FC<Select2Props> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  isMulti = false,
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
  className = "",
  menuPlacement = 'auto'
}) => {
  // Custom styles to match Tailwind gray theme
  const customStyles: StylesConfig<Option, boolean> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      border: state.isFocused ? '1px solid #6B7280' : '1px solid #D1D5DB',
      borderRadius: '6px',
      backgroundColor: 'white',
      boxShadow: state.isFocused 
        ? '0 0 0 4px rgba(156, 163, 175, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.1)' 
        : provided.boxShadow,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: '#9CA3AF',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      cursor: 'pointer'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 12px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: '#D1D5DB',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      '& > div': {
        padding: '8px',
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#6B7280' : '#9CA3AF',
      '&:hover': {
        color: '#6B7280',
      },
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#6B7280' : '#9CA3AF',
      '&:hover': {
        color: '#EF4444',
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '6px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #E5E7EB',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#6B7280' 
        : state.isFocused 
        ? '#F3F4F6' 
        : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '8px 12px',
      borderRadius: '4px',
      margin: '1px 0',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#6B7280' : '#F3F4F6',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '14px',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px',
    }),
  };

  // Find the selected option with type-safe comparison
  const selectedOption = options.find(option => {
    // Handle both string and number comparisons
    return option.value?.toString() === value?.toString();
  }) || null;

  const handleChange = (
    newValue: SingleValue<Option> | MultiValue<Option>
  ) => {
    if (isMulti) {
      // Handle multi-select (not implemented in this example)
      onChange(null);
    } else {
      const singleValue = newValue as SingleValue<Option>;
      onChange(singleValue ? singleValue.value : null);
    }
  };

  return (
    <div className={className}>
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        styles={customStyles}
        menuPlacement={menuPlacement}
        classNamePrefix="react-select"
      />
    </div>
  );
};

export default Select2;
